import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import courseService from "../../../services/courseService";
import { getYearOptions } from "../../../utils/courseYears";

const EMPTY_FORM = {
    courseName: "",
    shortName: "",
    courseCode: "",
    durationYears: "",
    semestersPerYear: "",
    isActive: true,
};

export default function CourseForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(id);

    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(isEdit);

    useEffect(() => {
        if (!isEdit) return;
        (async () => {
            try {
                const res = await courseService.getById(id);
                const c = res.data;
                setForm({
                    courseName: c.courseName || "",
                    shortName: c.shortName || "",
                    courseCode: c.courseCode || "",
                    durationYears: c.durationYears ?? "",
                    semestersPerYear: c.semestersPerYear ?? "",
                    isActive: c.isActive ?? true,
                });
            } catch (err) {
                toast.error(err.message);
                navigate("/settings/courses");
            } finally {
                setFetching(false);
            }
        })();
    }, [id, isEdit, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const validate = () => {
        const nextErrors = {};
        if (!form.courseName.trim()) nextErrors.courseName = "Course name is required";
        if (!form.shortName.trim()) nextErrors.shortName = "Short name is required";
        if (!form.courseCode.trim()) nextErrors.courseCode = "Course code is required";
        if (!form.durationYears || Number(form.durationYears) < 1) {
            nextErrors.durationYears = "Enter a valid duration (in years)";
        }
        if (!form.semestersPerYear || Number(form.semestersPerYear) < 1) {
            nextErrors.semestersPerYear = "Enter a valid number of semesters per year";
        }
        return nextErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const nextErrors = validate();
        if (Object.keys(nextErrors).length) {
            setErrors(nextErrors);
            toast.error(Object.values(nextErrors)[0]);
            return;
        }

        const payload = {
            courseName: form.courseName.trim(),
            shortName: form.shortName.trim(),
            courseCode: form.courseCode.trim(),
            durationYears: Number(form.durationYears),
            semestersPerYear: Number(form.semestersPerYear),
            isActive: form.isActive,
        };

        setLoading(true);
        try {
            if (isEdit) {
                await courseService.update(id, payload);
                toast.success("Course updated.");
            } else {
                await courseService.create(payload);
                toast.success("Course added.");
            }
            navigate("/settings/courses");
        } catch (err) {
            toast.error(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    };

    const previewYears = getYearOptions({ durationYears: Number(form.durationYears) || 0 });

    if (fetching) {
        return (
            <div className="card">
                <div className="loading-center">
                    <div className="spinner" />
                    Loading course...
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">{isEdit ? "Edit Course" : "Add Course"}</h1>
                    <p className="page-subtitle">Year labels (1st Year, 2nd Year, ...) are generated automatically from duration.</p>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="card">
                    <div className="card-body">
                        <div className="form-grid">
                            <div className="form-group">
                                <label className="form-label">
                                    Course Name<span style={{ color: "var(--danger)" }}> *</span>
                                </label>
                                <input
                                    className={`form-control ${errors.courseName ? "error" : ""}`}
                                    name="courseName"
                                    placeholder="e.g. Bachelor of Pharmacy"
                                    value={form.courseName}
                                    onChange={handleChange}
                                />
                                {errors.courseName && <span className="form-error">{errors.courseName}</span>}
                            </div>

                            <div className="form-group">
                                <label className="form-label">
                                    Short Name<span style={{ color: "var(--danger)" }}> *</span>
                                </label>
                                <input
                                    className={`form-control ${errors.shortName ? "error" : ""}`}
                                    name="shortName"
                                    placeholder="e.g. B. Pharm"
                                    value={form.shortName}
                                    onChange={handleChange}
                                />
                                {errors.shortName && <span className="form-error">{errors.shortName}</span>}
                                <span className="form-hint">Used as the display label everywhere — Students, Fee Structures, receipts.</span>
                            </div>

                            <div className="form-group">
                                <label className="form-label">
                                    Course Code<span style={{ color: "var(--danger)" }}> *</span>
                                </label>
                                <input
                                    className={`form-control ${errors.courseCode ? "error" : ""}`}
                                    name="courseCode"
                                    placeholder="e.g. BPHARM"
                                    value={form.courseCode}
                                    onChange={handleChange}
                                    style={{ textTransform: "uppercase" }}
                                />
                                {errors.courseCode && <span className="form-error">{errors.courseCode}</span>}
                                <span className="form-hint">Must be unique. Saved in uppercase.</span>
                            </div>

                            <div className="form-group">
                                <label className="form-label">
                                    Duration (years)<span style={{ color: "var(--danger)" }}> *</span>
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    className={`form-control ${errors.durationYears ? "error" : ""}`}
                                    name="durationYears"
                                    value={form.durationYears}
                                    onChange={handleChange}
                                />
                                {errors.durationYears && <span className="form-error">{errors.durationYears}</span>}
                            </div>

                            <div className="form-group">
                                <label className="form-label">
                                    Semesters per Year<span style={{ color: "var(--danger)" }}> *</span>
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    className={`form-control ${errors.semestersPerYear ? "error" : ""}`}
                                    name="semestersPerYear"
                                    value={form.semestersPerYear}
                                    onChange={handleChange}
                                />
                                {errors.semestersPerYear && <span className="form-error">{errors.semestersPerYear}</span>}
                                <span className="form-hint">Usually 2 — kept editable since it can vary by course.</span>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Status</label>
                                <select
                                    className="form-control"
                                    value={form.isActive ? "active" : "inactive"}
                                    onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.value === "active" }))}
                                >
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                        </div>

                        {previewYears.length > 0 && (
                            <div style={{ marginTop: "1rem", padding: "0.75rem 0.85rem", background: "#f8fafc", borderRadius: 8 }}>
                                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: "0.4rem" }}>
                                    YEAR LABELS PREVIEW
                                </div>
                                <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                                    {previewYears.map((y) => (
                                        <span key={y.yearNumber} className="badge badge-blue">
                                            {y.label}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "1.25rem" }}>
                            <button type="button" className="btn btn-outline" onClick={() => navigate("/settings/courses")}>
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={loading}>
                                {loading ? "Saving..." : isEdit ? "Update Course" : "Add Course"}
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}